/**
 * **The telemetry sink — the thing this app was blind without.**
 *
 * R1-5 left two seams that *look* like telemetry and ship nothing:
 * `installUnhandledRejectionLogging()` (`main.tsx`) and `ErrorBoundary.componentDidCatch`
 * (`components/ErrorBoundary.tsx`). Both `console.error` and stop. **On a store build that is
 * written to nobody** — `native/AGENTS.md` shows the only capture path is
 * `xcrun devicectl … --console`, attached at launch, on a device somebody is physically holding.
 *
 * That is not a theoretical gap. A production iOS resident was locked out of sign-in by their own
 * device; the fix was reinstalling the app, which **replaced the container and destroyed the
 * specimen**, so the `DOMException.name` that would have named the damage was never recovered.
 * D309 named the cause from a recurrence instead — iOS kills the WKWebView's Networking process
 * and every IndexedDB connection with it — which is exactly the kind of thing this module exists so
 * that nobody has to reconstruct from an access log again.
 *
 * ---
 *
 * **THE PAYLOAD CARRIES NO IDENTIFIER, AND THAT IS A COMPLIANCE BOUNDARY RATHER THAN A
 * PREFERENCE.** D111 removed standing human read access to production personal data and keeps
 * CloudTrail data events off by design; D109 mints four statutory artifacts maintained as one set
 * with `docs/compliance/d73-engagement.md`, and an unmirrored change to any of the five is a
 * defect. **A sink whose payload carries a user id or a device id is processing personal data**
 * and drags in that five-artifact mirror plus counsel — a different piece of work, and not one to
 * start on the run (`AGENTS.md` §3, D255).
 *
 * So: error name, rung, counts, elapsed time, build, platform. Nothing that resolves to a person.
 * `telemetry.test.ts` asserts the absence rather than trusting this paragraph.
 *
 * **Why the two R1-5 seams above are not wired to this in the same change.** They are the obvious
 * next callers and they are deliberately deferred: an arbitrary exception *message* is not
 * identifier-free — a rejected request's error can carry the phone number that was being
 * submitted — so routing them here needs a scrubbing design and a decision about what a message
 * class is. Shipping that untested would put personal data in the payload on the first render
 * error, which is the one thing this module may not do. **Six event types ship** — the
 * credential-store failure, D306's two (a refresh the server refused, with the one bit only the
 * device knows, and a boot that landed on the memory store), D309's lost storage connection, the
 * boot credential read that rejected (`credential-read-failed`, 2026-09-25), and a sign-in whose
 * account-switch purge could not run (`sign-in-purge-failed`, 2026-10-16). Each is a fixed,
 * identifier-free shape; the transport is general and waits for the scrubbing design above.
 */

import { ROUTES } from '@selogate/core'
import { nativeApiTransport } from '../auth/nativeHttp'
import { fetchTransport } from '../auth/transport'
import { isNativeContainer } from '../pwa/nativeContainer'
import { BUILD } from '../pwa/version'

/**
 * Which rung of the recovery ladder resolved a credential-store failure — **and the reason this
 * field is worth more than the error name beside it.**
 *
 * | Observed | Means |
 * |---|---|
 * | rung 1 resolves it, always | The damage is "the database needs rebuilding". Ladder correct. |
 * | rung 1 never does, rung 2 does | The damage is in the connection handle, not the data. |
 * | Neither resolves it | The failure was never storage-shaped; the ladder is the wrong tree. |
 */
export type RecoveryRung = 'rung1' | 'rung2' | 'none'

export interface CredentialStoreFailureEvent {
  /** The **first** failure's `DOMException.name` — the cause, before any rung changed the state. */
  readonly errorName: string
  readonly rung: RecoveryRung
  /** How many `outbox` rows the best-effort salvage recovered before the destroy. */
  readonly salvagedReceipts: number
  /** Whether replaying them into the rebuilt store succeeded. */
  readonly replayOk: boolean
  readonly elapsedMs: number
}

/**
 * `'ios' | 'android' | 'web'`, read from the Capacitor bridge the same way
 * `auth/nativeHttp.ts`, `ble/nativeBluetooth.ts` and `device-capabilities/contacts.ts` read it —
 * **the platform is read, never discovered by rejection.** A browser has no bridge and is `web`.
 */
function platform(): string {
  if (!isNativeContainer()) return 'web'
  const bridge = (globalThis as { Capacitor?: { getPlatform?: () => string } }).Capacitor
  return typeof bridge?.getPlatform === 'function' ? bridge.getPlatform() : 'unknown'
}

/**
 * The transport, and **it must be D166's door rather than a bare `fetch` — on iOS a bare `fetch`
 * reports nothing at all, silently, on the one platform this sink exists for.**
 *
 * This was written as `fetch(...)` first and that was a defect. An API Gateway HTTP API **refuses a
 * non-`http(s)` scheme in `allowOrigins`** (`BadRequestException: Invalid format for origin`,
 * measured), so the iOS WebView's `capacitor://<host>` origin **cannot be admitted by CORS on any
 * stage** — a platform fact, not a configuration choice. From the app's own WebView the refusal is
 * witnessed: preflight `204`, `access-control-allow-origin` absent, app code sees `Load failed`.
 * Capacitor's app-wide `fetch` patch is deliberately **off** (D166, D260(d) name turning it on as
 * the edit to refuse), so the global `fetch` really is the platform's own.
 *
 * The failure mode is what makes this worth a paragraph: the `catch` below would have swallowed it
 * into a `console.warn` **that nothing on a store build is written to**, so the sink would have
 * looked healthy while reporting nothing from iOS forever — the same shape as the blindness it was
 * built to remove, one layer down.
 *
 * `nativeApiTransport` is imported rather than restated: it already answers *iOS, in a native
 * container* and routes through `CapacitorHttp`, falling back to `fetchTransport` everywhere else.
 * **Android needs none of it** — `androidScheme: 'https'` (D161(c)) makes its origin byte-identical
 * to the web origin, so its WebView's own stack reaches the API exactly as a browser's does, and
 * rerouting it would trade a working path for an untested one.
 */
const transport = nativeApiTransport(fetchTransport)

/**
 * Send one diagnostic. **Fire-and-forget, and it never rejects into its caller.**
 *
 * Every caller of this is already on a failing path, and a diagnostic that can throw would turn a
 * recovered sign-in into a failed one — the sink reporting a problem by causing a worse one. So
 * the returned promise always resolves, and the only trace of a failed send is a console line.
 */
function send(body: Record<string, unknown>): Promise<void> {
  return transport({ method: 'POST', path: ROUTES.clientDiagnostic.template, body })
    .then(() => undefined)
    .catch((error: unknown) => {
      // Not rethrown, and not silent either: on a device with the console attached this is the
      // difference between "the sink reported nothing" and "the sink could not reach the API".
      console.warn('diagnostic-send-failed', error)
    })
}

/**
 * **Emitted on every credential-store failure, including one rung 1 recovered silently.**
 *
 * That is the entire point and it is worth stating plainly, because the cheaper thing to build
 * reports only unrecovered failures: a silent recovery that reports nothing is the *same*
 * blindness that cost this incident its evidence, automated and without a human in the loop. It
 * is also the only way the rate this defect has no denominator for ever gets one.
 */
export function reportCredentialStoreFailure(event: CredentialStoreFailureEvent): Promise<void> {
  return send({
    event: 'credential-store-failure',
    errorName: event.errorName,
    rung: event.rung,
    salvagedReceipts: event.salvagedReceipts,
    replayOk: event.replayOk,
    elapsedMs: Math.round(event.elapsedMs),
    appVersion: BUILD.commit,
    platform: platform(),
  })
}

export interface RefreshRefusedEvent {
  /**
   * D152's `racedInterruptedAttempt`: the durable refresh marker named the very family id this
   * refresh presented, so a previous attempt with that id was interrupted before it could record
   * an outcome. **This is the field that splits a self-inflicted replay from a genuine one**, and
   * D152 computed it and wrote it to a `console.warn` that reaches nobody on a store build.
   */
  readonly interruptedAttempt: boolean
}

/**
 * **D306 — `/auth/refresh` answered `401`, and this device is about to end its session over it.**
 *
 * The server counts the refusal itself (`RefreshRefused`, `RefreshFamilyMismatch`); what it
 * cannot know is whether the family id it just refused was one the device had *already presented
 * once and never heard back about* — the signature of an unacknowledged rotation, where the
 * server retired the credential and the response carrying its replacement was lost. Only the
 * device's own marker knows that, so only the device can report it. No family id travels: it is a
 * per-session secret's sibling, and the operator needs the bit, not the value.
 */
export function reportRefreshRefused(event: RefreshRefusedEvent): Promise<void> {
  return send({
    event: 'refresh-refused',
    interruptedAttempt: event.interruptedAttempt,
    appVersion: BUILD.commit,
    platform: platform(),
  })
}

export interface StoreConnectionLostEvent {
  /**
   * Which of the two signals recognised the loss. **This is the field that decides whether the
   * message match can ever be retired**: WebKit PR #60094 relies on the `close` event, and
   * reporters on iOS 17.4–18.x say it did not fire reliably before the 26.5 fix. A distribution
   * that is all `close-event` on a cohort below 26.5 would mean the fragment list is dead weight;
   * all `error-shape` would mean removing it re-opens the defect.
   */
  readonly signal: 'close-event' | 'error-shape'
  /**
   * `reloaded` — this document took the one reload the app process allows. `declined` — it was
   * already taken, or there is no bridge to take it from (the browser PWA), so the resident fell
   * to the OTP screen and paid a D65/D88 cap slot.
   */
  readonly outcome: 'reloaded' | 'declined'
}

/**
 * **The silent sign-out, reported for the first time.**
 *
 * Before this, a refresh that succeeded and whose credential write was refused went straight to
 * `endSession('storage-lost')` — no rung ran, neither `reportCredentialStoreFailure` nor
 * `reportRefreshRefused` fired, and the only trace anywhere was a device that verified an OTP and
 * then made no request of any kind. That absence *was* the diagnosis, once, by hand, from the
 * production access log; it is not a way to measure a rate.
 *
 * Emitted on **both** outcomes, for the reason the ladder's event is emitted on a silent recovery:
 * a reload that heals the resident invisibly and reports nothing is the same blindness one layer
 * out. `outcome` beside `signal` is what answers whether the rung is reaching anybody.
 */
export function reportStoreConnectionLost(event: StoreConnectionLostEvent): Promise<void> {
  return send({
    event: 'store-connection-lost',
    signal: event.signal,
    outcome: event.outcome,
    appVersion: BUILD.commit,
    platform: platform(),
  })
}

export interface StoreOpenDegradedEvent {
  /** The open rejected, or fired nothing inside `STORE_OPEN_TIMEOUT_MS`. Different damage. */
  readonly outcome: 'failed' | 'timed-out'
  /** The rejection's `DOMException.name`; `none` for a timeout, which carries no exception. */
  readonly errorName: string
  readonly elapsedMs: number
}

/**
 * **D306 — this launch could not open IndexedDB and is running on a memory store.**
 *
 * The device will land signed out at the OTP screen with nothing to present, and from the server
 * that is indistinguishable from a phone that never existed: no request, no audit row, nothing.
 * `session-eviction-handoff.md` §2 names three ways a device arrives with no credentials, and this
 * is the only one that is cheap to separate — one event, on the path that already degrades.
 */
export function reportStoreOpenDegraded(event: StoreOpenDegradedEvent): Promise<void> {
  return send({
    event: 'store-open-degraded',
    outcome: event.outcome,
    errorName: event.errorName,
    elapsedMs: Math.round(event.elapsedMs),
    appVersion: BUILD.commit,
    platform: platform(),
  })
}

export interface CredentialReadFailedEvent {
  /**
   * `retrying` — the FIRST read of a restore run rejected and the manager is reading again; filed
   * at once so a run the OS kills mid-way still leaves a trace. `recovered` — a later read in the
   * same run succeeded, so the resident never saw the sign-in screen. `exhausted` — every read in
   * the budget rejected and the sign-in screen is showing the storage line. **The rate of this
   * class is the count of `retrying`; how often retrying helps is `recovered` over that.** Neither
   * needs an identifier, which is why the pair is two events rather than one stitched by a run id.
   */
  readonly outcome: 'retrying' | 'recovered' | 'exhausted'
  /** The latest rejection's `DOMException.name`, or `DegradedToMemory` for a reopen that fell to memory. */
  readonly errorName: string
  /** Since the run started, so `exhausted` reads the budget actually spent. */
  readonly elapsedMs: number
  /** 1-based: which read this was — the one that failed, or for `recovered` the one that worked. */
  readonly attempt: number
  /** `boot` for the launch's own run, `retry` for the sign-in screen's Try again. */
  readonly trigger: 'boot' | 'retry'
}

/**
 * **The boot-time credential read rejected — the 2026-09-25 class, reported for the first time.**
 *
 * A read that rejects is not an empty store, and the manager now re-reads rather than landing on the
 * OTP screen (`SessionManager.#doRestore`). Before this, that rejection went to a `console.warn` and
 * the resident was shown a sign-in screen over a session still on the phone — no request, no event,
 * nothing on the server — which is why its one production occurrence had to be read by elimination
 * from the access log and CloudTrail. Same identifier-free shape as `store-open-degraded`.
 */
export function reportCredentialReadFailed(event: CredentialReadFailedEvent): Promise<void> {
  return send({
    event: 'credential-read-failed',
    outcome: event.outcome,
    errorName: event.errorName,
    elapsedMs: Math.round(event.elapsedMs),
    attempt: event.attempt,
    trigger: event.trigger,
    appVersion: BUILD.commit,
    platform: platform(),
  })
}

export interface SignInPurgeFailedEvent {
  /** The rejection's `DOMException.name` — `InvalidStateError` for the 2026-10-16 occurrence. */
  readonly errorName: string
  /**
   * Which of D309's two signals recognised a lost storage connection, or `none` when the failure was
   * not recognised as one. **This is what says whether the occurrence was the Networking-process
   * kill or something else**, which the error name alone cannot: a handle we closed ourselves throws
   * the same `InvalidStateError`.
   */
  readonly signal: 'close-event' | 'error-shape' | 'none'
}

/**
 * **A sign-in whose credentials were SAVED, and whose account-switch purge could not run
 * (2026-10-16).**
 *
 * The keystore write succeeded (D339), then the IndexedDB read behind `purgeOtherAccounts` threw on
 * a dead WebKit storage connection, and that throw used to reach the code screen as *reinstall the
 * app* over a session that was already on the phone — the resident paid a second SMS, and a
 * force-quit later signed them in with none. Nothing reached the server, which is why the diagnosis
 * was read by hand from the access log. The sign-in now completes; this event is the only trace that
 * the purge was skipped, and the next launch's best-effort purge is what finishes it.
 */
export function reportSignInPurgeFailed(event: SignInPurgeFailedEvent): Promise<void> {
  return send({
    event: 'sign-in-purge-failed',
    errorName: event.errorName,
    signal: event.signal,
    appVersion: BUILD.commit,
    platform: platform(),
  })
}
